<template>
  <v-container fluid>
    <PageTitle icon="fas fa-fw fa-inbox">
      Emails
      <template v-slot:buttons>
        <v-menu v-model="showSearchForm"
                :close-on-content-click="false"
                offset-y
                bottom
                left
                width="300px"
                nudge-bottom="5px">
          <template v-slot:activator="{on, attrs}">
            <v-btn title="Search"
                   :disabled="form.busy"
                   v-bind="attrs"
                   v-on="on"
                   class="mr-2">
              <v-icon left>
                fas fa-fw fa-search
              </v-icon>
              Search
              <v-icon right>
                fas fa-caret-down
              </v-icon>
            </v-btn>
          </template>
          <v-form @submit.prevent="search"
                  :disabled="form.busy">
            <v-card>
              <v-card-text>

                <v-select v-model="timePeriod"
                          label="Time period"
                          outlined
                          dense
                          :items="timePeriods"
                          item-text="text"
                          return-object
                          @change="selectTimePeriod">
                </v-select>

                <v-row>
                  <v-col cols="6">
                    <v-text-field v-model="form.start_at"
                                  type="date"
                                  label="Start date"
                                  outlined
                                  dense
                                  :min="minStartDate"
                                  :max="maxStartDate"
                                  required
                                  :error-messages="form.errors.get('start_at')">
                    </v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="form.end_at"
                                  type="date"
                                  label="End date"
                                  outlined
                                  dense
                                  :min="minEndDate"
                                  :max="maxEndDate"
                                  required
                                  :error-messages="form.errors.get('end_at')">
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-autocomplete v-model="form.sender_id"
                                label="Sender"
                                outlined
                                dense
                                :items="users"
                                item-text="name"
                                item-value="id"
                                clearable
                                @change="search">
                </v-autocomplete>

                <v-text-field v-model="form.recipient"
                              label="Recipient"
                              outlined
                              dense
                              placeholder="Email address contains"
                              clearable>
                </v-text-field>

                <v-autocomplete v-model="form.group_id"
                                label="Group"
                                outlined
                                dense
                                :items="groups"
                                item-text="name"
                                item-value="id"
                                clearable
                                @change="search">
                </v-autocomplete>

                <v-autocomplete v-model="form.share_template_id"
                                label="Email Template"
                                outlined
                                dense
                                :items="shareTemplates"
                                item-text="name"
                                item-value="id"
                                clearable
                                @change="search">
                  <template v-slot:item="{item: template}">
                    <template v-if="template.is_active">
                      {{ template.name }}
                    </template>
                    <template v-else>
                      INACTIVE - {{ template.name }}
                    </template>

                  </template>
                </v-autocomplete>

                <v-text-field v-model="form.subject"
                              label="Subject"
                              outlined
                              dense
                              placeholder="Subject contains"
                              clearable>
                </v-text-field>

                <v-select v-model="form.status"
                          label="Message Status"
                          outlined
                          dense
                          :items="statusCodes"
                          item-text="text"
                          item-value="id"
                          clearable
                          @change="search">
                </v-select>

                <v-select v-model="form.is_sharing_email"
                          label="Is Sharing Email"
                          outlined
                          dense
                          :items="yesNoOptions"
                          item-text="text"
                          item-value="value"
                          @change="search">
                </v-select>

                <v-autocomplete v-model="form.tags"
                                label="Tag"
                                outlined
                                dense
                                multiple
                                :items="tags"
                                item-text="name"
                                item-value="id"
                                clearable
                                chips
                                small-chips
                                deletable-chips>
                </v-autocomplete>

                <v-select v-model="form.per_page"
                          label="Show"
                          outlined
                          dense
                          :items="itemsPerPage"
                          item-text="text"
                          item-value="id"
                          @change="search"
                          hide-details>
                </v-select>

              </v-card-text>
              <v-card-actions class="px-4">
                <v-btn type="submit"
                       color="primary"
                       :disabled="form.busy">
                  Search
                </v-btn>
                <v-btn @click.prevent="resetForm"
                       text
                       :disabled="form.busy">
                  Reset
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
        </v-menu>

        <v-btn v-if="canExportData"
               :disabled="form.busy"
               @click.prevent="exportMessages">
          <v-icon left>
            fas fa-fw fa-download
          </v-icon>
          Download
        </v-btn>
      </template>
    </PageTitle>

    <v-card>
      <MailgunMessagesTable :form="form"
                            :loading="form.busy"
                            :messages="messages"
                            :server-items-length="serverItemsLength"
                            :per-page="perPage"
                            @update:items-per-page="updateItemsPerPage"
                            @update:page="updatePage">
      </MailgunMessagesTable>
    </v-card>

  </v-container>
</template>
<script>
import PageTitle from '../../../components/PageTitle.vue';
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import mailgunService from '../../../../../ml/services/mailgunService';
import {find} from 'lodash';
import DOMPurify from 'dompurify';
import moment from 'moment-timezone'
import MailgunMessagesTable from './components/MailgunMessagesTable.vue';

const dateFormat = 'YYYY-MM-DD';

export default {
  components: {MailgunMessagesTable, PageTitle},
  data() {
    return {
      statusCodes: mailgunService.getStatusCodes(),
      activities: mailgunService.activities(), // MLD-1742
      dateRangeMenu: false,
      defaultPerPage: 50,
      defaultTimePeriod: 'Past 30 days',
      perPage: 50,
      form: new Form(mailgunService.initMessagesForm()),
      options: {},
      pagination: null,
      messages: [],
      itemsPerPage: [
        {id: 10, text: '10 emails'},
        {id: 25, text: '25 emails'},
        {id: 50, text: '50 emails'},
        {id: 100, text: '100 emails'},
        {id: 500, text: '500 emails'},
      ],
      events: mailgunService.events(),
      orderFields: [
        {id: 'created_at', text: 'Date'},
        {id: 'recipient', text: 'Recipient'},
        {id: 'subject', text: 'Subject'},
      ],
      orderDirs: [
        {id: 'asc', text: 'Ascending'},
        {id: 'desc', text: 'Descending'},
      ],
      yesNoOptions: [ // MLD-6818
        {value: '', text: 'Yes or No'},
        {value: true, text: 'Yes'},
        {value: false, text: 'No'},
      ],
      operations: 0,
      serverItemsLength: 0,
      showSearchForm: false,
      tags: [],
      timePeriod: null,
      today: moment().endOf('day'),
    }
  },
  computed: {
    ...mapGetters([
      'canExportData',
      'groups',
      'isAdmin',
      'isMember',
      'shareTemplates', // MLD-6865
      'team',
      'users',
      'user',
    ]),
    timePeriods() {
      return mailgunService.timePeriods();
    },
    minStartDate() {
      if (this.team) {
        return moment(this.team.created_at).format('YYYY-MM-DD');
      }
      return null;
    },
    maxStartDate() {
      return this.today.format('YYYY-MM-DD');
    },
    minEndDate() {
      return this.form.start_at;
    },
    maxEndDate() {
      return this.today.format('YYYY-MM-DD');
    },
  },
  created() {
    this.fetchData();
    this.resetForm();
  },
  methods: {
    fetchData() {
      this.fetchTags();
    },
    fetchTags() {
      return mailgunService.tags()
        .then(data => {
          this.tags = data;
        })
        .catch(() => {
        });
    },
    selectTimePeriod() {
      if (this.timePeriod) {
        this.changeTimePeriod(this.timePeriod);
      } else {
        this.resetForm();
      }
    },
    resetForm() {
      this.timePeriod = find(this.timePeriods, t => t.text === this.defaultTimePeriod);
      this.form = new Form(mailgunService.initMessagesForm(this.defaultPerPage));
      this.changeTimePeriod(this.timePeriod);
    },
    changeTimePeriod(timePeriod) {
      if (this.form.busy) {
        this.$toast.warning('Busy - please wait');
        return;
      }
      const startPeriod = timePeriod.start;
      const endPeriod = timePeriod.end;
      const start = moment().add(startPeriod.units, startPeriod.period).startOf(startPeriod.period);
      const end = moment().add(endPeriod.units, endPeriod.period).endOf(endPeriod.period);
      this.form.start_at = start.format(dateFormat);
      this.form.end_at = end.format(dateFormat);
      this.updatePage(1);
    },
    recipients(message) {
      if (message.recipients && message.recipients.data) {
        return message.recipients.data.map(w => w.recipient_email);
      }
      return [];
    },
    async search() {
      if (this.form.busy) {
        this.$toast.warning('Busy - please wait');
        return;
      }
      return this.form.post(`/api/mailgun/messages/search`)
        .then(({data}) => {
          this.perPage = this.form.per_page;
          this.messages = data?.data;
          this.pagination = data?.meta?.pagination
          this.serverItemsLength = data?.meta?.pagination?.total;
          this.operations = this.operations + 1;
          return data?.data;
        })
        .catch(err => {
          if (err.response && err.response.data.error) {
            this.$toast.error(DOMPurify.sanitize(err.response.data.error));
          }
        })
        .finally(() => {
          this.form.busy = false;
          this.form.export = false;
        })
    },
    tableUpdated(options) {
      console.log('tableUpdated', options)
      this.form.page = options.page;
      this.search();
    },
    exportMessages() {
      const form = this.form.data();
      this.form.startProcessing();
      return mailgunService.exportMessages(form)
        .then(() => {
          this.form.finishProcessing();
        })
        .catch(err => {
          this.$toast.error('There was a problem.');
        })
        .finally(() => {
          this.form.busy = false;
        })
    },
    updateItemsPerPage(itemsPerPage) {
      this.form.page = 1;
      this.form.per_page = itemsPerPage;
      this.search();
    },
    updatePage(page) {
      this.form.page = page;
      this.search();
    }
  }
}
</script>
